import React, { useState } from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Typography,
  Button,
  Chip,
  Grid,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import { TOKENS, fmtMoney } from '../theme';

// Spec §5 — Raffle identity-verification audit. Dual-photo side-by-side
// comparison (stored ID vs validation photo taken at the counter).
export default function RaffleAuditMode({
  open,
  onClose,
  onComplete,
  raffles,
  decisions,
  auditIndex,
  onIndexChange,
  onDecision,
}) {
  const [zoomPhoto, setZoomPhoto] = useState(null);

  if (!open || !raffles?.length) return null;
  const raffle = raffles[auditIndex];
  if (!raffle) return null;
  const myDecision = decisions[`RAFFLE:${raffle.raffleId}`];
  const reviewedCount = Object.keys(decisions).filter((k) =>
    k.startsWith('RAFFLE:')
  ).length;

  const idPhotoUrl = raffle.photo?.[0]?.localURL;
  const validationPhotoUrl = raffle.files?.[0]?.localURL;

  const isLast = auditIndex === raffles.length - 1;
  const goPrev = () => onIndexChange(Math.max(0, auditIndex - 1));
  const goNext = () =>
    onIndexChange(Math.min(raffles.length - 1, auditIndex + 1));
  // Last item: finishing the audit returns to the previous screen and lets
  // the caller surface an "Audit completed" confirmation.
  const handleComplete = () => (onComplete ? onComplete() : onClose());
  const handleApprove = () => {
    onDecision(raffle, 'APPROVED');
    setTimeout(goNext, 0);
  };
  const handleFlag = () => {
    onDecision(raffle, 'FLAGGED');
    setTimeout(goNext, 0);
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <AppBar
        position="relative"
        sx={{
          background: '#fff',
          color: TOKENS.slate900,
          boxShadow: 0,
          borderBottom: `1px solid ${TOKENS.slate200}`,
        }}
      >
        <Toolbar sx={{ minHeight: 48 }}>
          <IconButton edge="start" onClick={onClose}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                color: TOKENS.slate500,
              }}
            >
              Raffle Audit
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
              {auditIndex + 1} of {raffles.length}
            </Typography>
          </Box>
          <Typography
            sx={{ fontSize: 10, fontWeight: 700, color: TOKENS.slate500 }}
          >
            {reviewedCount}/{raffles.length}
          </Typography>
        </Toolbar>
        <Box
          sx={{ height: 4, background: TOKENS.slate200, position: 'relative' }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              width: `${(reviewedCount / raffles.length) * 100}%`,
              background: TOKENS.emerald600,
              transition: 'width 0.2s',
            }}
          />
        </Box>
      </AppBar>
      <Box sx={{ flex: 1, overflowY: 'auto', background: TOKENS.slate50 }}>
        {myDecision && (
          <Box
            sx={{
              px: 1.5,
              py: 0.75,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              background:
                myDecision === 'APPROVED' ? TOKENS.emerald50 : TOKENS.red50,
              borderBottom: `1px solid ${
                myDecision === 'APPROVED' ? TOKENS.emerald200 : TOKENS.red200
              }`,
            }}
          >
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background:
                  myDecision === 'APPROVED' ? TOKENS.emerald600 : TOKENS.red600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {myDecision === 'APPROVED' ? (
                <CheckIcon sx={{ color: '#fff', fontSize: 14 }} />
              ) : (
                <CloseIcon sx={{ color: '#fff', fontSize: 14 }} />
              )}
            </Box>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color:
                  myDecision === 'APPROVED' ? TOKENS.emerald700 : TOKENS.red700,
              }}
            >
              {myDecision === 'APPROVED'
                ? 'Identity verified · Approved'
                : 'Identity mismatch · Flagged'}
            </Typography>
          </Box>
        )}
        <Box sx={{ p: 2 }}>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: TOKENS.slate500,
            }}
          >
            Customer
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700, mt: 0.25 }}>
            {raffle.name || '—'}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mt: 1,
              flexWrap: 'wrap',
            }}
          >
            <Chip
              icon={<EmojiEventsIcon style={{ color: TOKENS.pink700 }} />}
              label={fmtMoney(raffle.amount)}
              sx={{
                background: TOKENS.pink50,
                color: TOKENS.pink900,
                fontWeight: 700,
                border: `1px solid ${TOKENS.pink200}`,
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeIcon sx={{ color: TOKENS.slate500, fontSize: 14 }} />
              <Typography sx={{ fontSize: 11, fontFamily: 'monospace' }}>
                {raffle.time}
              </Typography>
            </Box>
            {raffle.winnerNumber && (
              <Chip
                size="small"
                label={`Winner #${raffle.winnerNumber}`}
                sx={{
                  height: 20,
                  fontSize: 11,
                  fontWeight: 700,
                  background: TOKENS.pink50,
                  color: TOKENS.pink700,
                  border: `1px solid ${TOKENS.pink200}`,
                }}
              />
            )}
            <Chip
              size="small"
              label={`#${auditIndex + 1}`}
              sx={{ height: 18, fontSize: 9, background: TOKENS.slate100 }}
            />
          </Box>
        </Box>
        <Box sx={{ px: 2 }}>
          <Box
            sx={{
              background: TOKENS.amber50,
              border: `1px solid ${TOKENS.amber200}`,
              borderRadius: 2,
              p: 1.25,
              mb: 1.5,
              display: 'flex',
              gap: 1,
            }}
          >
            <WarningAmberIcon sx={{ color: TOKENS.amber700, fontSize: 18 }} />
            <Typography
              sx={{ fontSize: 11, color: TOKENS.amber900, fontWeight: 500 }}
            >
              Verify both photos show the <strong>same person</strong> before
              approving.
            </Typography>
          </Box>
          <Grid container spacing={1} sx={{ maxWidth: 900, mx: 'auto' }}>
            <PhotoCard
              label="ID Picture"
              sub="Stored in system"
              src={idPhotoUrl}
              borderColor={TOKENS.blue200}
              chipBg={TOKENS.blue50}
              chipText={TOKENS.blue700}
              onZoom={() =>
                idPhotoUrl &&
                setZoomPhoto({ src: idPhotoUrl, label: 'ID Picture' })
              }
            />
            <PhotoCard
              label="Validation"
              sub="Taken at raffle"
              src={validationPhotoUrl}
              borderColor={TOKENS.emerald200}
              chipBg={TOKENS.emerald50}
              chipText={TOKENS.emerald700}
              onZoom={() =>
                validationPhotoUrl &&
                setZoomPhoto({ src: validationPhotoUrl, label: 'Validation' })
              }
            />
          </Grid>
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: 10,
              color: TOKENS.slate500,
              mt: 1,
            }}
          >
            Tap any photo to zoom in
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: TOKENS.slate500,
              mb: 1,
            }}
          >
            Do both photos show the same person?
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant={myDecision === 'FLAGGED' ? 'contained' : 'outlined'}
                onClick={handleFlag}
                sx={{
                  py: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  lineHeight: 1.1,
                  background:
                    myDecision === 'FLAGGED' ? TOKENS.red600 : TOKENS.red50,
                  color: myDecision === 'FLAGGED' ? '#fff' : TOKENS.red700,
                  borderColor: TOKENS.red200,
                  '&:hover': {
                    background:
                      myDecision === 'FLAGGED' ? TOKENS.red700 : TOKENS.red100,
                  },
                }}
              >
                <CloseIcon />
                <span>No · Flag</span>
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApprove}
                sx={{
                  py: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  lineHeight: 1.1,
                  background: TOKENS.emerald600,
                  '&:hover': { background: TOKENS.emerald700 },
                }}
              >
                <CheckIcon />
                <span>Yes · Approve</span>
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Box
        sx={{
          p: 1.5,
          borderTop: `1px solid ${TOKENS.slate200}`,
          background: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Button
          startIcon={<ChevronLeftIcon />}
          disabled={auditIndex === 0}
          onClick={goPrev}
        >
          Prev
        </Button>
        <Typography sx={{ fontSize: 10, color: TOKENS.slate500 }}>
          {auditIndex + 1} / {raffles.length}
        </Typography>
        {isLast ? (
          <Button
            variant="contained"
            endIcon={<CheckIcon />}
            onClick={handleComplete}
            sx={{
              background: TOKENS.emerald600,
              fontWeight: 700,
              '&:hover': { background: TOKENS.emerald700 },
            }}
          >
            Audit Completed
          </Button>
        ) : (
          <Button endIcon={<ChevronRightIcon />} onClick={goNext}>
            Next
          </Button>
        )}
      </Box>
      <Dialog
        open={!!zoomPhoto}
        onClose={() => setZoomPhoto(null)}
        fullScreen
        PaperProps={{ sx: { background: '#000' } }}
      >
        <AppBar
          position="fixed"
          sx={{
            background: 'rgba(0,0,0,0.6)',
            boxShadow: 0,
            zIndex: 1,
          }}
        >
          <Toolbar sx={{ minHeight: 56 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13, color: '#fff' }}>
                {raffle.name}
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
                {zoomPhoto?.label}
              </Typography>
            </Box>
            <IconButton onClick={() => setZoomPhoto(null)}>
              <CloseIcon sx={{ color: '#fff' }} />
            </IconButton>
          </Toolbar>
        </AppBar>
        {/* Full-viewport scroll container. We size the image off the
            viewport (100vw / 100vh - toolbar) directly so it never depends
            on parent flex height resolving, which is fragile inside nested
            MUI Dialogs. touchAction enables pinch-zoom on touchscreens. */}
        <Box
          sx={{
            width: '100vw',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'auto',
            pt: '56px',
            pb: 2,
            px: { xs: 1, sm: 2 },
            background: '#000',
            touchAction: 'pinch-zoom',
          }}
        >
          {zoomPhoto?.src && (
            <img
              src={zoomPhoto.src}
              alt={zoomPhoto?.label || ''}
              style={{
                maxWidth: 'calc(100vw - 16px)',
                maxHeight: 'calc(100vh - 72px)',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                display: 'block',
                borderRadius: 4,
                userSelect: 'none',
              }}
            />
          )}
        </Box>
      </Dialog>
    </Dialog>
  );
}

function PhotoCard({ label, sub, src, borderColor, chipBg, chipText, onZoom }) {
  return (
    <Grid item xs={6}>
      <Box
        onClick={onZoom}
        sx={{
          background: '#fff',
          border: `2px solid ${borderColor}`,
          borderRadius: 1,
          overflow: 'hidden',
          cursor: src ? 'zoom-in' : 'default',
        }}
      >
        <Box
          sx={{
            background: chipBg,
            px: 1,
            py: 0.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: 9,
              fontWeight: 700,
              textTransform: 'uppercase',
              color: chipText,
            }}
          >
            {label}
          </Typography>
          {src && <ZoomInIcon sx={{ fontSize: 14, color: chipText }} />}
        </Box>
        {/* Fixed-ratio frame so both cards stay the same size, but show
            the FULL image (objectFit: contain) — never crop. Landscape 4:3
            on every viewport with a viewport-relative max-height cap so the
            card never grows excessively tall on either phone or desktop. */}
        <Box
          sx={{
            aspectRatio: '4/3',
            maxHeight: '45vh',
            background: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {src && (
            <Box
              component="img"
              src={src}
              alt={label}
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          )}
        </Box>
        <Typography
          sx={{
            px: 1,
            py: 0.5,
            textAlign: 'center',
            fontSize: 9,
            color: TOKENS.slate500,
            background: TOKENS.slate50,
          }}
        >
          {sub}
        </Typography>
      </Box>
    </Grid>
  );
}
